import { useEffect } from "react";
import DaltonHeroSection from "./components/HeroSection";
import DaltonStickyNav from "./components/StickyNav";
import DaltonProcessingTimeSection from "./components/ProcessingTimeSection";
import DaltonServicesSection from "./components/ServicesSection";
import DaltonLocalAreaSection from "./components/LocalAreaSection";
import DaltonTestimonialsSection from "./components/TestimonialsSection";
import DaltonAcceptanceFacilitiesSection from "./components/AcceptanceFacilitiesSection";
import DaltonComparisonSection from "./components/ComparisonSection";
import DaltonMistakesSection from "./components/MistakesSection";
import DaltonHowItWorksSection from "./components/HowItWorksSection";
import DaltonFAQSection, { faqs } from "./components/FAQSection";
import DaltonHelpfulResourcesSection from "./components/HelpfulResourcesSection";
import DaltonFinalCTA from "./components/FinalCTA";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

export default function DaltonPage() {
  useEffect(() => {
    document.title = "Emergency & Expedited Passport Dalton GA | Same-Day Service | Fast Passport Center";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) {
        el = document.createElement("link");
        el.setAttribute("rel", rel);
        document.head.appendChild(el);
      }
      el.setAttribute("href", href);
    };

    const pageUrl = "https://fastpassportcenter.com/dalton/";

    setLink("canonical", pageUrl);
    setMeta("description", "Emergency and expedited passport services in Dalton, GA — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Whitfield County and northwest Georgia travelers. Get your emergency passport fast!");
    setMeta("geo.placename", "Dalton, Georgia");
    setMeta("geo.region", "US-GA");
    setMeta("geo.position", "34.7698;-84.9702");
    setMeta("ICBM", "34.7698, -84.9702");
    setMeta("keywords", "expedited passport, emergency passport, Dalton passport, same day passport Dalton, fast passport Georgia, Whitfield County passport");

    const servicesList = [
      { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
      { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
      { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
      { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
      { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
      { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
      { name: "Second Passport & Damaged", description: "Second passport issuance for frequent travelers and damaged passport replacement." },
    ];

    const graph = [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://fastpassportcenter.com/" },
          { "@type": "ListItem", position: 2, name: "Georgia", item: "https://fastpassportcenter.com/georgia/" },
          { "@type": "ListItem", position: 3, name: "Dalton Passport Services", item: pageUrl },
        ],
      },
      {
        "@type": "LocalBusiness",
        name: "Fast Passport Center",
        description: "Fast Passport Center does not have a physical office in Dalton — we serve this area remotely. Emergency and expedited passport services as fast as 24 hours for Dalton and northwest Georgia travelers.",
        url: pageUrl,
        telephone: "+1-877-253-0084",
        areaServed: {
          "@type": "City",
          name: "Dalton",
          containedInPlace: {
            "@type": "State",
            name: "Georgia",
          },
        },
        priceRange: "$$$",
        openingHours: ["Mo-Fr 09:00-18:00"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "1200",
        },
      },
      ...servicesList.map((s) => ({
        "@type": "Service",
        serviceType: s.name,
        provider: {
          "@type": "LocalBusiness",
          name: "Fast Passport Center",
        },
        areaServed: {
          "@type": "City",
          name: "Dalton",
        },
        description: s.description,
      })),
      {
        "@type": "FAQPage",
        mainEntity: faqs.map((faq) => ({
          "@type": "Question",
          name: faq.q,
          acceptedAnswer: {
            "@type": "Answer",
            text: faq.a,
          },
        })),
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    document.getElementById("structured-data")?.remove();
    script.textContent = JSON.stringify({ "@context": "https://schema.org", "@graph": graph });
    document.head.appendChild(script);

    return () => {
      const existing = document.getElementById("structured-data");
      if (existing) existing.remove();
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
    };
  }, []);

  return (
    <div className="font-sans">
      <DaltonHeroSection />
      <DaltonStickyNav />
      <DaltonProcessingTimeSection />
      <DaltonServicesSection />
      <DaltonLocalAreaSection />
      <DaltonTestimonialsSection />
      <DaltonAcceptanceFacilitiesSection />
      <DaltonComparisonSection />
      <DaltonMistakesSection />
      <DaltonHowItWorksSection />
      <DaltonFAQSection />
      <DaltonHelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/dalton" state="GA" />
      <DaltonFinalCTA />
    </div>
  );
}